import type { ApiResponse } from '@/types/api';
import type { DialogPublicInfo } from '@/types/dialog';
import { captureError } from '@/lib/error-tracking';
import { isCapturableHttpStatus } from '@/lib/error-scrub';

let config: { baseUrl: string } | null = null;

async function getBaseUrl(): Promise<string> {
  if (config) return config.baseUrl;
  const res = await fetch(`${import.meta.env.BASE_URL}config.json`);
  if (!res.ok) throw new Error(`Failed to load config: HTTP ${res.status}`);
  const cfg = await res.json();
  const baseUrl = cfg?.api?.baseUrl;
  if (typeof baseUrl !== 'string') throw new Error('Invalid config: missing api.baseUrl');
  config = { baseUrl };
  return config.baseUrl;
}

async function publicRequest<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
  const baseUrl = await getBaseUrl();
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  if (!res.ok) {
    // 4xx (invalid/expired token) is expected respondent flow; only server
    // faults are signal. The invite token in `path` is redacted by the
    // event scrubber before anything leaves the browser.
    if (isCapturableHttpStatus(res.status)) {
      captureError(new Error(`HTTP ${res.status} ${path}`), {
        source: 'dialog-http',
        status: String(res.status),
      });
    }
    // Try to surface the backend's error message rather than just the status.
    // Falls back to "HTTP {status}" if the body is missing or unparsable.
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body && typeof body.message === 'string' && body.message.trim()) {
        message = body.message;
      }
    } catch {
      // body wasn't JSON — stick with the status code fallback
    }
    return { success: false, data: null, message };
  }
  return res.json();
}

export const dialogApi = {
  getDialogByToken: (token: string, clientSessionId?: string) =>
    publicRequest<DialogPublicInfo>(
      `/d/${token}${clientSessionId ? `?clientSessionId=${encodeURIComponent(clientSessionId)}` : ''}`,
    ),

  batchSubmit: (
    token: string,
    data: {
      // Per-session id for anonymous submitters, so two un-named anonymous
      // respondents don't collapse into one in synthesis counts.
      clientSessionId?: string;
      items: Array<{
        id: string;
        type: 'job' | 'challenge' | 'opportunity';
        title: string;
        description?: string;
        parentJobIds: string[];
        edited?: boolean;
      }>;
    },
  ) =>
    publicRequest<{
      nodes: Array<{ draftId: string; nodeId: string; nodeType: string; title: string }>;
      edges: Array<{ edgeId: string; sourceNodeId: string; targetNodeId: string; edgeType: string }>;
    }>(`/d/${token}/batch-submit`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};
